import React, { useState, useEffect, useCallback, useRef } from 'react';
import portalAPI from '../../services/portalAPI';
import {
  PortalInvoice,
  getStatusColor,
  getStatusLabel
} from '../../services/quickbooksService';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import PrintableInvoice from '../../components/Portal/PrintableInvoice';
import PaymentOptionsModal, { PaymentSubmitParams } from './PaymentOptionsModal';
import {
  AttemptRecordBody, PaymentAttempt, PaymentRail, armLoadWatchdog, attemptRecordBody,
  createAttempt, isHelcimMessageOrigin, parseHelcimMessage, resolveHelcimIframe,
  transactionIsAch,
} from './helcimHandoff';

// reCAPTCHA site key - loaded from environment
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Credits interface
interface PortalCredit {
  id: string;
  credit_type: string;
  description: string;
  amount: number;
  member_name: string;
  member_code: string;
  status: 'pending' | 'applied';
  created_at: string;
  applied_at: string | null;
  order_number: string | null;
  invoice_number: string | null;
  invoice_id: number | null;
}

// Customer credit (prepayments/refunds/adjustments) — separate from Bridge
// Club store credits. Read-only in the portal.
interface CustomerCreditApplicationEntry {
  id: number;
  amount_applied: string;
  applied_at: string | null;
  credit_type: string;
  credit_type_display: string;
  invoice_id: number;
  invoice_number: string;
}

interface CustomerCreditMemoEntry {
  id: number;
  memo_number: string;
  memo_date: string | null;
  total_credit: string;
  credit_type: string;
  credit_type_display: string;
  application_mode: string;
  reference_invoice_number: string | null;
  sent_at: string | null;
}

interface CustomerCreditBlock {
  balance: string;
  applications: CustomerCreditApplicationEntry[];
  credits: Array<{
    id: number;
    amount: string;
    credit_type: string;
    credit_type_display: string;
    reference: string;
    amount_remaining: string;
    created_at: string | null;
  }>;
  credit_memos?: CustomerCreditMemoEntry[];
}

interface CreditsData {
  pending_total: number;
  pending_count: number;
  applied_total: number;
  applied_count: number;
  credits: PortalCredit[];
  // New: customer credit block (always present from the API).
  customer_credit?: CustomerCreditBlock;
}

// HelcimPay.js types are declared in src/types/helcim.d.ts

// Extend window for grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Every portal checkout is minted cc-ach — both rails — because Helcim rejects
// a single-rail checkout carrying the Fee Saver fee with a 400 ("Convenience
// fee can only be applied when both payment methods exist"). The backend
// (quickbooks_views.portal_invoice_pay) pins that; nothing here can ask for
// anything else. One constant, so the attempt object and every telemetry
// record it produces say the same true thing about what was minted.
const MINTED_RAIL: PaymentRail = 'all';

const PortalBilling: React.FC = () => {
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoiceId, setPayingInvoiceId] = useState<number | null>(null);
  // The customer's CURRENT attempt to pay: created only after a checkout was
  // minted, and carried by closure into the watchdog, the Helcim message
  // handler and every telemetry record it produces. A later click creates a
  // NEW attempt and can never rewrite this one. (Until 2026-09-09 the rail
  // lived in one shared ref that every click overwrote, so a record posted
  // 20 seconds later described the latest click — two card attempts were
  // recorded as method=ach that way.) See helcimHandoff.ts.
  const activeAttemptRef = useRef<PaymentAttempt | null>(null);
  const [filter, setFilter] = useState<'all' | 'unpaid' | 'paid' | 'credits'>('all');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const recaptchaScriptLoaded = useRef(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PortalInvoice | null>(null);
  const [fullInvoiceData, setFullInvoiceData] = useState<any>(null);
  const [fullInvoiceLoading, setFullInvoiceLoading] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);

  // Split-payment options modal — opened by any Pay Now click.
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalInvoice, setPaymentModalInvoice] = useState<PortalInvoice | null>(null);

  // Credits state
  const [creditsData, setCreditsData] = useState<CreditsData | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsFilter, setCreditsFilter] = useState<'all' | 'pending' | 'applied'>('all');

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (recaptchaScriptLoaded.current || !RECAPTCHA_SITE_KEY) return;
    recaptchaScriptLoaded.current = true;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaReady(true);
      });
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount (optional)
      // We typically leave it since other pages might use it
    };
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await portalAPI.get('/portal/invoices/');
      // Handle both array response and object with invoices property
      const data = Array.isArray(response.data) ? response.data : (response.data.invoices || []);
      setInvoices(data);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      setMessage({ type: 'error', text: 'Failed to load invoices' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCredits = useCallback(async () => {
    setCreditsLoading(true);
    try {
      const response = await portalAPI.get('/portal/credits/');
      setCreditsData(response.data);
    } catch (error) {
      console.error('Failed to load credits:', error);
      // Silently fail - credits may not be available for all users
      setCreditsData(null);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    loadCredits();
  }, [loadInvoices, loadCredits]);

  // Download a credit-memo PDF. Streams as a blob through the authenticated
  // portal client (cookie auth) so the IDOR-scoped endpoint stays protected.
  const downloadMemoPdf = useCallback(async (memoId: number) => {
    try {
      const response = await portalAPI.get(`/portal/credit-memos/${memoId}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
      // Revoke shortly after so the new tab has time to load the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Failed to download credit memo PDF:', error);
      setMessage({ type: 'error', text: 'Could not open the credit memo PDF. Please try again.' });
    }
  }, []);

  // Load full invoice data when an invoice is selected
  const loadFullInvoice = useCallback(async (invoiceId: number) => {
    setFullInvoiceLoading(true);
    setFullInvoiceData(null);
    try {
      const response = await portalAPI.get(`/portal/invoices/${invoiceId}/full/`);
      setFullInvoiceData(response.data);
    } catch (error) {
      console.error('Failed to load full invoice:', error);
      // Fallback - show basic modal without full data
    } finally {
      setFullInvoiceLoading(false);
    }
  }, []);

  // When an invoice is selected, load its full data
  const handleInvoiceClick = (invoice: PortalInvoice) => {
    setSelectedInvoice(invoice);
    setShowPrintView(false);
    loadFullInvoice(invoice.id);
  };

  // Listen for HelcimPay.js messages: SUCCESS, ABORTED, HIDE (the only three
  // it sends — see helcimHandoff.ts). The origin check is an exact-host trust
  // boundary. Until 2026-09-09 this gate was a substring test for helcim.com;
  // the widget posts from https://secure.helcim.app, so the portal never heard
  // a single message: no success recorded, no decline, no close, and the
  // watchdog below fired on every widget that opened.
  useEffect(() => {
    const handleHelcimMessage = async (event: MessageEvent) => {
      if (!isHelcimMessageOrigin(event.origin)) return;
      const msg = parseHelcimMessage(event.data);
      if (!msg || !msg.status) return;

      // Helcim spoke — whatever it said, the window is alive. Stand the
      // watchdog down before anything else.
      clearHelcimWatchdog();

      const attempt = activeAttemptRef.current;
      if (!attempt) {
        // HIDE also arrives when the customer dismisses the confirmation
        // screen after a SUCCESS, by which point the attempt is closed.
        return;
      }
      const { invoiceId } = attempt;
      const { payload } = msg;
      const flagKey = `invoice_${invoiceId}_payment_initiated`;

      if (msg.status === 'SUCCESS') {
        activeAttemptRef.current = null;
        setPayingInvoiceId(null);
        const response = (payload.response ?? {}) as Record<string, unknown>;
        const transactionId = payload.transactionId ?? payload.transId ?? response.transactionId;
        const amount = payload.amount ?? response.amount;
        const cardType = String(payload.cardType ?? response.cardType ?? '');
        const isAch = transactionIsAch(attempt.rail, payload);
        try {
          if (!transactionId || !amount) {
            console.error('Helcim SUCCESS message missing transactionId/amount', payload);
            throw new Error('missing transaction details');
          }
          // Idempotent server-side: this endpoint, the Helcim webhook, the
          // Gmail receipt task and the ACH poller all check
          // QBPayment.helcim_transaction_id before writing.
          await portalAPI.post(`/portal/invoices/${invoiceId}/record-helcim-payment/`, {
            transactionId,
            amount,
            type: isAch ? 'ach' : 'card',
            cardType,
          });
          localStorage.removeItem(flagKey);
          setMessage({
            type: 'success',
            text: isAch
              ? 'ACH payment initiated! Your bank transfer is processing (3-5 business days).'
              : 'Payment successful! Your invoice has been paid.',
          });
        } catch (recordError) {
          console.error('Failed to record payment:', recordError);
          // Helcim took the payment; the webhook or the receipt task will
          // record it. Keep the flag so the button stays disabled meanwhile.
          setMessage({ type: 'success', text: 'Payment successful! Your invoice status will update shortly.' });
        }
        // Helcim keeps its confirmation screen up until the customer closes
        // it (a HIDE follows). Never tear the iframe down under a confirmation.
        loadInvoices();
        return;
      }

      // ABORTED (Helcim: the payment was declined / not completed) and HIDE
      // (the customer closed the window). UI only: tear down, clear the
      // debounce flag, record an abandon at info level so Sentry does not
      // wake for normal behaviour. Never a payment record on either.
      activeAttemptRef.current = null;
      setPayingInvoiceId(null);
      localStorage.removeItem(flagKey);
      window.removeHelcimPayIframe?.(); // start.js already removes it on HIDE; no-op then
      if (msg.status === 'ABORTED') {
        const errorText = String(payload.eventMessage ?? payload.error ?? payload.message ?? '');
        postAttempt(invoiceId, attemptRecordBody(attempt, 'abandoned', 'helcim_aborted', errorText || undefined));
        setMessage({
          type: 'error',
          text: 'The payment was not completed. Nothing has been charged. You can try again, '
              + 'or call us at (509) 738-2287 and we can take the payment over the phone.',
        });
      } else {
        postAttempt(invoiceId, attemptRecordBody(attempt, 'abandoned', 'window_closed'));
      }
    };

    window.addEventListener('message', handleHelcimMessage);
    return () => window.removeEventListener('message', handleHelcimMessage);
    // clearHelcimWatchdog / postAttempt are stable useCallbacks (empty deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadInvoices]);

  // Debounce flag for the Pay Now button — prevents double-clicking the
  // /pay/ endpoint while the first checkout-mint round trip is in flight.
  //
  // Scope: button-click debounce only. UI rendering of "Payment Processing"
  // is driven by backend invoice.status === 'ach_pending', not by this
  // flag. (Earlier versions used this flag for UI; that locked customers
  // out for 15 min when they cancelled the modal.)
  //
  // 60s window is plenty for the API round trip. The helcim-pay-close /
  // helcim-pay-error handlers above clear the flag promptly on modal
  // exit; the timeout is a safety net only.
  const isPaymentInitiated = (invoiceId: number): boolean => {
    try {
      const flag = localStorage.getItem(`invoice_${invoiceId}_payment_initiated`);
      if (!flag) return false;
      const timestamp = parseInt(flag, 10);
      const sixtySeconds = 60 * 1000;
      if (Date.now() - timestamp < sixtySeconds) return true;
      // Expired — clean up
      localStorage.removeItem(`invoice_${invoiceId}_payment_initiated`);
      return false;
    } catch {
      return false;
    }
  };

  // Check if an ACH payment is stale (>7 days with no clearing)
  const isAchStale = (invoice: PortalInvoice): boolean => {
    if (invoice.status !== 'ach_pending') return false;
    if (!invoice.ach_initiated_at) return true; // No timestamp = definitely stale
    const initiated = new Date(invoice.ach_initiated_at).getTime();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - initiated > sevenDays;
  };

  // ── Payment-attempt telemetry ────────────────────────────────────────
  // Until this existed, only SUCCESSES reached the server: QBPayment has no
  // status field and a row is written solely on a completed payment, so a
  // decline — or a Helcim window that never opened — left no trace anywhere.
  // Invoice 123 sat unpaid across two attempts in a month and Red Bridge only
  // found out because Sibyl phoned the customer.
  //
  // The body (outcome, reason, detail, amount, payment_method) is built by
  // helcimHandoff.attemptRecordBody from the attempt it belongs to. Never
  // throws and never blocks: this is telemetry on a path that is already
  // failing, and it must not become a second reason the customer cannot pay.
  const postAttempt = useCallback((invoiceId: number, body: AttemptRecordBody) => {
    portalAPI
      // body.payment_method is MINTED_RAIL: the rail actually minted, not one
      // this page requested. The mint response carries no rail of its own.
      .post(`/portal/invoices/${invoiceId}/payment-attempt/`, body)
      .catch(() => { /* telemetry must never surface to the customer */ });
  }, []);

  // How long we give Helcim's iframe to LOAD before calling it dead. This
  // measures one thing only — did the payment window appear — and is cleared
  // by the iframe's load event (helcimHandoff.armLoadWatchdog). It says
  // nothing about the customer, who may take as long as they like once the
  // window is up. The previous watchdog waited for a Helcim *message*, which
  // only comes when the customer finishes, so it fired on every open widget
  // at 20 seconds, tore it down mid-payment and said nothing was charged.
  //
  // 20s sits above a bad-network cold load of a third-party iframe (seconds,
  // not tens of seconds) and well below a human's patience.
  const HELCIM_LOAD_TIMEOUT_MS = 20_000;

  // Cancel function for the armed watchdog; null when none is armed.
  const helcimWatchdogRef = useRef<(() => void) | null>(null);

  const clearHelcimWatchdog = useCallback(() => {
    helcimWatchdogRef.current?.();
    helcimWatchdogRef.current = null;
  }, []);

  // Pay Now (table row / mobile card / detail modal) now opens the
  // split-payment options modal instead of immediately minting a checkout.
  // The actual /pay/ call happens in handlePaymentSubmit once the customer
  // has chosen an amount + rail.
  const handlePayNow = (invoiceId: number) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;
    setMessage(null);
    setPaymentModalInvoice(invoice);
    setShowPaymentModal(true);
  };

  // Called by PaymentOptionsModal "Continue to Payment". Mints the Helcim
  // checkout for the chosen amount and rail, then hands off to HelcimPay.js.
  // Rejects on failure so the modal can surface the error inline and stay
  // open for a retry.
  const handlePaymentSubmit = async ({ amount }: PaymentSubmitParams) => {
    const invoice = paymentModalInvoice;
    if (!invoice) return;
    const invoiceId = invoice.id;
    const flagKey = `invoice_${invoiceId}_payment_initiated`;
    // Telemetry for a failure BEFORE a checkout exists. Once a checkout is
    // minted, the attempt object below owns the body instead.
    const preMintFailure = (reason: string, detail?: string): AttemptRecordBody =>
      attemptRecordBody({ rail: MINTED_RAIL, amount }, 'failed', reason, detail);

    // GUARD: Prevent double-click / re-payment (60s window)
    if (isPaymentInitiated(invoiceId)) {
      throw new Error('A payment was just initiated for this invoice. Please wait a few minutes before trying again.');
    }

    // GUARD: HelcimPay.js is a third-party script that a corporate network or
    // content blocker can refuse. Catch that HERE, with a message naming the
    // cause — rather than falling through to window.open() for a popup a
    // blocker eats just as silently.
    if (window.__HELCIM_PAY_FAILED__) {
      postAttempt(invoiceId, preMintFailure('helcim_script_blocked',
                                            'HelcimPay.js failed to load in the customer browser'));
      throw new Error(
        'Our payment provider could not load in this browser — it is usually a '
        + 'network or ad-blocker restriction. Try another browser or network, '
        + 'or call us at (509) 738-2287 and we can take the payment directly.'
      );
    }

    setPayingInvoiceId(invoiceId);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      if (RECAPTCHA_SITE_KEY && recaptchaReady && window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'payment' });
        } catch (recaptchaError) {
          console.error('reCAPTCHA execution failed:', recaptchaError);
          // Continue without token - backend will handle missing token appropriately
        }
      }

      // No payment_method: the backend decides the rail and always mints
      // both. Sending one here is what produced the ach-only checkouts Helcim
      // rejected with a 400.
      const response = await portalAPI.post(`/portal/invoices/${invoiceId}/pay/`, {
        recaptcha_token: recaptchaToken,
        amount,
      });

      const { checkoutToken, payment_link, payment_url } = response.data;

      if (checkoutToken && window.appendHelcimPayIframe) {
        // ONE attempt, created now that a checkout exists, and carried by
        // closure into everything below. A later click on any invoice makes
        // a new attempt; it cannot reach into this one.
        const attempt = createAttempt({ invoiceId, rail: MINTED_RAIL, amount, token: checkoutToken });
        activeAttemptRef.current = attempt;

        // Mark payment as initiated BEFORE opening Helcim checkout
        localStorage.setItem(flagKey, Date.now().toString());
        postAttempt(invoiceId, attemptRecordBody(attempt, 'started', 'checkout_minted'));

        // Checkout minted successfully — close the options modal and hand off.
        setShowPaymentModal(false);
        setPaymentModalInvoice(null);

        // Clear any iframe left over from a previous attempt before appending
        // a new one — a stale overlay can swallow the new session. Matches
        // useHelcimAddCard.ts, which has always done this.
        window.removeHelcimPayIframe?.();
        const iframe = resolveHelcimIframe(window.appendHelcimPayIframe(checkoutToken));

        // Ends THIS attempt as a failure. Guarded on identity: if a later
        // attempt already owns the page, this one is history and stays quiet.
        const failAttempt = (reason: string, detail: string, text: string) => {
          if (activeAttemptRef.current !== attempt) return;
          activeAttemptRef.current = null;
          window.removeHelcimPayIframe?.();
          localStorage.removeItem(flagKey);
          setPayingInvoiceId(null);
          postAttempt(invoiceId, attemptRecordBody(attempt, 'failed', reason, detail));
          setMessage({ type: 'error', text });
        };

        // MOUNT CHECK. appendChild is synchronous, so the only evidence the
        // call worked is the element it returned (or #helcimPayIframe).
        if (!iframe) {
          clearHelcimWatchdog();
          failAttempt('iframe_never_mounted',
                      'appendHelcimPayIframe returned without creating an iframe',
                      'The payment window could not open. Please try again, or call '
                      + 'us at (509) 738-2287 and we can take the payment over the phone.');
          return;
        }

        // WATCHDOG — on the iframe's load event, not on a Helcim message.
        // load fires when Helcim's document arrives; from then on the
        // customer may take as long as they like and nothing tears the
        // window down. Only a load that never comes is a failure.
        clearHelcimWatchdog();
        helcimWatchdogRef.current = armLoadWatchdog(iframe, HELCIM_LOAD_TIMEOUT_MS, {
          onLoaded: () => {
            attempt.loaded = true;
            helcimWatchdogRef.current = null;
          },
          onNeverLoaded: () => {
            helcimWatchdogRef.current = null;
            failAttempt('widget_never_loaded',
                        `Helcim's payment window did not load within ${HELCIM_LOAD_TIMEOUT_MS / 1000}s`,
                        'The payment window did not open. Nothing has been charged. '
                        + 'Please try again, or call us at (509) 738-2287 and we can take '
                        + 'the payment over the phone.');
          },
        });
      } else if (payment_link || payment_url) {
        // Fallback: hosted page in a new tab. A blocker can eat this too, so
        // say what should have happened rather than leaving a blank screen.
        const opened = window.open(payment_link || payment_url, '_blank');
        if (!opened) {
          localStorage.removeItem(flagKey);
          postAttempt(invoiceId, preMintFailure('popup_blocked',
                                                'window.open for the hosted payment page was blocked'));
          throw new Error(
            'Your browser blocked the payment window. Allow pop-ups for this site '
            + 'and try again, or call us at (509) 738-2287.'
          );
        }
      } else {
        throw new Error('No payment method available');
      }
    } catch (error: unknown) {
      console.error('Failed to get payment link:', error);
      clearHelcimWatchdog();
      // If checkout generation failed, clear the debounce flag so the
      // customer can retry immediately after adjusting their choice.
      localStorage.removeItem(flagKey);
      const axiosErr = error as {
        response?: {
          status?: number;
          data?: { error?: string };
          headers?: Record<string, string | undefined>;
        };
        message?: string;
      };
      const observedStatus = axiosErr?.response?.status;
      // DigitalOcean's edge stamps the origin's status on the response
      // (x-do-orig-status). Readable only if the edge exposes it to CORS —
      // record it when present, never assume it.
      const originStatus = axiosErr?.response?.headers?.['x-do-orig-status'];
      const statusTag = observedStatus !== undefined
        ? `HTTP ${observedStatus}${originStatus ? ` (origin ${originStatus})` : ''}: `
        : '';
      const detail = statusTag + (
        axiosErr?.response?.data?.error || axiosErr?.message || 'unknown'
      );
      postAttempt(invoiceId, preMintFailure('checkout_mint_failed', String(detail)));
      // Re-throw so PaymentOptionsModal shows the error inline and stays open
      // (the page-level banner is hidden behind the modal overlay).
      throw error;
    } finally {
      setPayingInvoiceId(null);
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num || 0);
  };

  // ACH-pending status banner — rendered when invoice.status === 'ach_pending'
  // and the timestamp isn't stale (>7 days). Both the table view and the
  // mobile card view use the same body; only the wrapping element differs.
  //
  // Why this matters: ACH submitted at 2pm doesn't leave the customer's bank
  // until Helcim's evening batch closes; the cleared webhook may not arrive
  // until the next business day. Without an explicit "Payment Processing"
  // affordance the customer sees a stale balance and can talk themselves into
  // paying again — which then takes weeks to refund. This banner is the
  // anti-double-pay guard.
  const renderAchPendingBanner = (invoice: PortalInvoice) => {
    const pendingAmount = formatCurrency(invoice.paid_amount || '0');
    const pendingDate = invoice.ach_initiated_at
      ? new Date(invoice.ach_initiated_at).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        })
      : 'recently';
    return (
      <div style={{
        background: '#ecfdf5',
        border: '1px solid #6ee7b7',
        borderRadius: 8,
        padding: '12px 14px',
        fontSize: '0.85rem',
        color: '#065f46',
        lineHeight: 1.5,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: '1rem', fontWeight: 700 }}>✓</span>
          <span style={{ fontWeight: 700 }}>Payment Processing</span>
        </div>
        <div>
          Your ACH payment of <strong>{pendingAmount}</strong> was received on {pendingDate}.
        </div>
        <div style={{ marginTop: 6, color: '#991b1b', fontWeight: 600 }}>
          Do not attempt to pay this invoice again.
        </div>
        <div style={{ marginTop: 4 }}>
          Your payment is being processed and will clear within 2–5 business days. You'll receive a confirmation email once the bank transfer completes.
        </div>
      </div>
    );
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate payment fees (Helcim Fee Saver rates)
  const calculateCardTotal = (amount: number) => {
    return (amount * 1.029 + 0.25).toFixed(2);
  };

  const calculateACHTotal = (amount: number) => {
    return amount.toFixed(2); // ACH is free — no processing fee
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'unpaid') {
      // Unpaid = has balance remaining
      const balance = parseFloat(inv.balance_due) || 0;
      return balance > 0;
    }
    if (filter === 'paid') {
      // Paid = no balance remaining
      const balance = parseFloat(inv.balance_due) || 0;
      return balance <= 0;
    }
    return true;
  });

  // Sum balance_due for all unpaid invoices
  const totalDue = invoices
    .filter(inv => parseFloat(inv.balance_due) > 0)
    .reduce((sum, inv) => sum + parseFloat(inv.balance_due), 0);

  const overdueCount = invoices.filter(inv => inv.is_overdue).length;

  return (
    <div style={styles.container} className={showPrintView ? 'print-overlay-active' : ''}>
      <TutorialAutoStart pageKey="billing" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.pageTitle}>My Billing</h1>
          <p style={styles.subtitle}>View and pay your invoices</p>
        </div>
        <TutorialHelpButton pageKey="billing" />
      </div>


      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          borderColor: message.type === 'success' ? '#86efac' : '#fecaca',
          color: message.type === 'success' ? '#166534' : '#dc2626',
        }}>
          {message.type === 'success' ? '\u2713' : '\u2717'} {message.text}
        </div>
      )}

      {/* Summary Cards */}
      <div style={styles.summaryCards} data-tutorial="balance-cards">
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Balance Due</span>
          <span style={styles.summaryValue}>{formatCurrency(totalDue)}</span>
        </div>
        <div style={{
          ...styles.summaryCard,
          ...(overdueCount > 0 ? styles.summaryCardWarning : {})
        }}>
          <span style={styles.summaryLabel}>Overdue Invoices</span>
          <span style={{
            ...styles.summaryValue,
            color: overdueCount > 0 ? '#dc2626' : '#22c55e'
          }}>
            {overdueCount}
          </span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Invoices</span>
          <span style={styles.summaryValue}>{invoices.length}</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterBar}>
        <button
          onClick={() => setFilter('all')}
          style={{
            ...styles.filterButton,
            ...(filter === 'all' ? styles.filterButtonActive : {})
          }}
        >
          All Invoices
        </button>
        <button
          onClick={() => setFilter('unpaid')}
          style={{
            ...styles.filterButton,
            ...(filter === 'unpaid' ? styles.filterButtonActive : {})
          }}
        >
          Unpaid
        </button>
        <button
          onClick={() => setFilter('paid')}
          style={{
            ...styles.filterButton,
            ...(filter === 'paid' ? styles.filterButtonActive : {})
          }}
        >
          Paid
        </button>
        {creditsData && (
          creditsData.pending_count > 0 ||
          creditsData.applied_count > 0 ||
          (creditsData.customer_credit && (
            parseFloat(creditsData.customer_credit.balance) !== 0 ||
            creditsData.customer_credit.applications.length > 0 ||
            (creditsData.customer_credit.credit_memos?.length ?? 0) > 0
          ))
        ) && (
          <button
            onClick={() => setFilter('credits')}
            style={{
              ...styles.filterButton,
              ...(filter === 'credits' ? styles.filterButtonActive : {}),
              ...(creditsData.pending_count > 0 ? { position: 'relative' as const } : {})
            }}
            data-tutorial="credits-tab"
          >
            Credits
            {creditsData.pending_count > 0 && (
              <span style={styles.creditsBadge}>{creditsData.pending_count}</span>
            )}
          </button>
        )}
      </div>

      {/* Invoice List - hide when showing credits */}
      {filter !== 'credits' && (
      <div style={styles.invoiceList} data-tutorial="invoice-table">
        {loading ? (
          <div style={styles.loading}>Loading invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 17V15M12 17V13M15 17V11M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p>No invoices found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Invoice #</th>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice, index) => (
                  <tr key={invoice.id} style={invoice.is_overdue ? styles.rowOverdue : {}}>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleInvoiceClick(invoice)}
                        style={styles.invoiceLink}
                      >
                        {invoice.invoice_number}
                      </button>
                      {invoice.status === 'paid' && (
                        <span style={styles.paidBadge}>PAID</span>
                      )}
                    </td>
                    <td style={styles.td}>#{invoice.order_number}</td>
                    <td style={styles.td}>{formatDate(invoice.invoice_date || invoice.date || invoice.created_at)}</td>
                    <td style={{
                      ...styles.td,
                      color: invoice.is_overdue ? '#dc2626' : 'inherit'
                    }}>
                      {formatDate(invoice.due_date)}
                      {invoice.is_overdue && <span style={styles.overdueTag}> Overdue</span>}
                    </td>
                    <td style={styles.td}>{formatCurrency(invoice.total || invoice.total_amount || '0')}</td>
                    <td style={{
                      ...styles.td,
                      fontWeight: 600,
                      color: parseFloat(invoice.balance_due) > 0 ? '#1f2937' : '#22c55e'
                    }}>
                      {formatCurrency(invoice.balance_due)}
                      {parseFloat(invoice.credits_applied || '0') > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 400 }}>
                          (incl. {formatCurrency(invoice.credits_applied)} Bridge Club credit)
                        </div>
                      )}
                      {parseFloat(invoice.customer_credits_applied || '0') > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 400 }}>
                          (incl. {formatCurrency(invoice.customer_credits_applied || '0')} account credit)
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(invoice.status) + '20',
                        color: getStatusColor(invoice.status)
                      }}>
                        {getStatusLabel(invoice.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {invoice.status === 'ach_pending' && !isAchStale(invoice) ? (
                        renderAchPendingBanner(invoice)
                      ) : invoice.status === 'ach_pending' && isAchStale(invoice) ? (
                        <>
                          <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: 4 }}>Previous payment attempt expired</div>
                          <button
                            onClick={() => handlePayNow(invoice.id)}
                            disabled={payingInvoiceId === invoice.id}
                            style={styles.payButton}
                          >
                            {payingInvoiceId === invoice.id ? 'Opening...' : 'Retry Payment'}
                          </button>
                        </>
                      ) : invoice.status === 'paid' ? null
                      : parseFloat(invoice.balance_due) > 0 && invoice.status !== 'draft' && invoice.status !== 'pending_review' ? (
                        <button
                          onClick={() => handlePayNow(invoice.id)}
                          disabled={payingInvoiceId === invoice.id}
                          style={styles.payButton}
                          data-tutorial={index === 0 ? "pay-btn" : undefined}
                          title={`Card: $${calculateCardTotal(parseFloat(invoice.balance_due))} (2.9% + $0.25)\nACH: $${calculateACHTotal(parseFloat(invoice.balance_due))} (free)`}
                        >
                          {payingInvoiceId === invoice.id ? 'Opening...' : 'Pay Now'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div style={styles.mobileCards}>
              {filteredInvoices.map(invoice => (
                <div
                  key={invoice.id}
                  style={{
                    ...styles.mobileCard,
                    ...(invoice.is_overdue ? styles.mobileCardOverdue : {})
                  }}
                >
                  <div style={styles.mobileCardHeader}>
                    <div>
                      <button
                        onClick={() => handleInvoiceClick(invoice)}
                        style={styles.mobileInvoiceLink}
                      >
                        {invoice.invoice_number}
                      </button>
                      <span style={styles.mobileOrder}>Order #{invoice.order_number}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {invoice.status === 'paid' && (
                        <span style={styles.paidBadge}>PAID</span>
                      )}
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(invoice.status) + '20',
                        color: getStatusColor(invoice.status)
                      }}>
                        {getStatusLabel(invoice.status)}
                      </span>
                    </div>
                  </div>

                  <div style={styles.mobileCardBody}>
                    <div style={styles.mobileRow}>
                      <span style={styles.mobileLabel}>Invoice Date</span>
                      <span>{formatDate(invoice.invoice_date || invoice.date || invoice.created_at)}</span>
                    </div>
                    <div style={styles.mobileRow}>
                      <span style={styles.mobileLabel}>Due Date</span>
                      <span style={{ color: invoice.is_overdue ? '#dc2626' : 'inherit' }}>
                        {formatDate(invoice.due_date)}
                        {invoice.is_overdue && <span style={styles.overdueTag}> Overdue</span>}
                      </span>
                    </div>
                    <div style={styles.mobileRow}>
                      <span style={styles.mobileLabel}>Total</span>
                      <span>{formatCurrency(invoice.total || invoice.total_amount || '0')}</span>
                    </div>
                    {parseFloat(invoice.credits_applied || '0') > 0 && (
                      <div style={styles.mobileRow}>
                        <span style={styles.mobileLabel}>Bridge Club Credit</span>
                        <span style={{ color: '#7c3aed' }}>-{formatCurrency(invoice.credits_applied)}</span>
                      </div>
                    )}
                    {parseFloat(invoice.customer_credits_applied || '0') > 0 && (
                      <div style={styles.mobileRow}>
                        <span style={styles.mobileLabel}>Account Credit</span>
                        <span style={{ color: '#7c3aed' }}>-{formatCurrency(invoice.customer_credits_applied || '0')}</span>
                      </div>
                    )}
                    <div style={styles.mobileRow}>
                      <span style={styles.mobileLabel}>Balance Due</span>
                      <strong style={{
                        color: parseFloat(invoice.balance_due) > 0 ? '#1f2937' : '#22c55e'
                      }}>
                        {formatCurrency(invoice.balance_due)}
                      </strong>
                    </div>
                  </div>

                  {invoice.status === 'ach_pending' && !isAchStale(invoice) ? (
                    <div style={styles.mobileCardFooter}>
                      {renderAchPendingBanner(invoice)}
                    </div>
                  ) : invoice.status === 'ach_pending' && isAchStale(invoice) ? (
                    <div style={styles.mobileCardFooter}>
                      <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: 4 }}>Previous payment attempt expired</div>
                      <button
                        onClick={() => handlePayNow(invoice.id)}
                        disabled={payingInvoiceId === invoice.id}
                        style={styles.payButtonMobile}
                      >
                        {payingInvoiceId === invoice.id ? 'Opening...' : 'Retry Payment'}
                      </button>
                    </div>
                  ) : parseFloat(invoice.balance_due) > 0 && invoice.status !== 'draft' && invoice.status !== 'pending_review' && invoice.status !== 'paid' && (
                    <div style={styles.mobileCardFooter}>
                      <div style={styles.feeEstimates}>
                        <span style={styles.feeEstimateItem}>
                          Card: ${calculateCardTotal(parseFloat(invoice.balance_due))}
                        </span>
                        <span style={styles.feeEstimateItem}>
                          ACH: ${calculateACHTotal(parseFloat(invoice.balance_due))} (free)
                        </span>
                      </div>
                      <button
                        onClick={() => handlePayNow(invoice.id)}
                        disabled={payingInvoiceId === invoice.id}
                        style={styles.payButtonMobile}
                      >
                        {payingInvoiceId === invoice.id ? 'Opening...' : 'Pay Now'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      )}

      {/* Credits Section */}
      {filter === 'credits' && creditsData && (
        <div style={styles.creditsSection}>
          {/* Account Credit — customer prepayments/refunds/adjustments */}
          {creditsData.customer_credit && (
            parseFloat(creditsData.customer_credit.balance) !== 0 ||
            creditsData.customer_credit.applications.length > 0
          ) && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: 20, marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Account Credit
                </div>
                <div style={{ fontSize: 30, fontWeight: 700, color: '#059669' }}>
                  {formatCurrency(parseFloat(creditsData.customer_credit.balance))}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  available — automatically applied to your invoices
                </div>
              </div>
              {creditsData.customer_credit.applications.length > 0 && (
                <div style={styles.creditsList}>
                  {creditsData.customer_credit.applications.map(app => (
                    <div key={app.id} style={styles.creditCard}>
                      <div style={styles.creditCardHeader}>
                        <strong style={styles.creditDescription}>{app.credit_type_display}</strong>
                        <span style={{ ...styles.creditStatusBadge, backgroundColor: '#d1fae5', color: '#065f46' }}>
                          Applied
                        </span>
                      </div>
                      <div style={styles.creditCardBody}>
                        <div style={styles.creditAmount}>{formatCurrency(parseFloat(app.amount_applied))}</div>
                        <div style={styles.creditMeta}>
                          {app.applied_at && <span>{formatDate(app.applied_at)}</span>}
                          <span style={styles.creditInvoiceLink}>Applied to Invoice #{app.invoice_number}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Credit Memos — formal CM-… documents (downloadable PDF) */}
          {creditsData.customer_credit?.credit_memos && creditsData.customer_credit.credit_memos.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                Credit Memos
              </div>
              <div style={styles.creditsList}>
                {creditsData.customer_credit.credit_memos.map(memo => (
                  <div key={memo.id} style={{ ...styles.creditCard, background: '#fffdf7', borderBottom: '1px solid #fde68a' }}>
                    <div style={styles.creditCardHeader}>
                      <strong style={styles.creditDescription}>{memo.credit_type_display}</strong>
                      <span style={{ ...styles.creditStatusBadge, backgroundColor: '#fef3c7', color: '#92400e' }}>
                        Credit Memo
                      </span>
                    </div>
                    <div style={styles.creditCardBody}>
                      <div style={styles.creditAmount}>{formatCurrency(parseFloat(memo.total_credit))}</div>
                      <div style={styles.creditMeta}>
                        <span>{memo.memo_number}</span>
                        {memo.memo_date && <span>{formatDate(memo.memo_date)}</span>}
                        {memo.reference_invoice_number && (
                          <span style={styles.creditInvoiceLink}>Ref Invoice #{memo.reference_invoice_number}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadMemoPdf(memo.id)}
                        style={{
                          marginTop: 10, padding: '8px 14px', minHeight: 40, fontSize: 13, fontWeight: 600,
                          background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
                        }}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bridge Club credits (retail partners) */}
          {(creditsData.pending_count > 0 || creditsData.applied_count > 0) && (
          <>
          {/* Credits Summary Cards */}
          <div style={styles.creditsSummaryCards}>
            <div style={styles.creditsSummaryCard}>
              <span style={styles.creditsSummaryLabel}>Pending Credits</span>
              <span style={styles.creditsSummaryValue}>{formatCurrency(creditsData.pending_total)}</span>
              <span style={styles.creditsSummaryCount}>{creditsData.pending_count} credit{creditsData.pending_count !== 1 ? 's' : ''}</span>
            </div>
            <div style={styles.creditsSummaryCard}>
              <span style={styles.creditsSummaryLabel}>Applied Credits</span>
              <span style={styles.creditsSummaryValue}>{formatCurrency(creditsData.applied_total)}</span>
              <span style={styles.creditsSummaryCount}>{creditsData.applied_count} credit{creditsData.applied_count !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Credits Sub-filter */}
          <div style={styles.creditsFilterBar}>
            <button
              onClick={() => setCreditsFilter('all')}
              style={{
                ...styles.creditsFilterBtn,
                ...(creditsFilter === 'all' ? styles.creditsFilterBtnActive : {})
              }}
            >
              All
            </button>
            <button
              onClick={() => setCreditsFilter('pending')}
              style={{
                ...styles.creditsFilterBtn,
                ...(creditsFilter === 'pending' ? styles.creditsFilterBtnActive : {})
              }}
            >
              Pending
            </button>
            <button
              onClick={() => setCreditsFilter('applied')}
              style={{
                ...styles.creditsFilterBtn,
                ...(creditsFilter === 'applied' ? styles.creditsFilterBtnActive : {})
              }}
            >
              Applied
            </button>
          </div>

          {/* Credits List */}
          <div style={styles.creditsList}>
            {creditsLoading ? (
              <div style={styles.loading}>Loading credits...</div>
            ) : creditsData.credits.filter(c =>
              creditsFilter === 'all' || c.status === creditsFilter
            ).length === 0 ? (
              <div style={styles.emptyState}>
                <p>No {creditsFilter !== 'all' ? creditsFilter : ''} credits found</p>
              </div>
            ) : (
              creditsData.credits
                .filter(c => creditsFilter === 'all' || c.status === creditsFilter)
                .map(credit => (
                  <div key={credit.id} style={styles.creditCard}>
                    <div style={styles.creditCardHeader}>
                      <div>
                        <strong style={styles.creditDescription}>{credit.description}</strong>
                        {credit.member_name && (
                          <span style={styles.creditMember}>{credit.member_name}</span>
                        )}
                      </div>
                      <span style={{
                        ...styles.creditStatusBadge,
                        backgroundColor: credit.status === 'pending' ? '#fef3c7' : '#d1fae5',
                        color: credit.status === 'pending' ? '#92400e' : '#065f46',
                      }}>
                        {credit.status === 'pending' ? 'Pending' : 'Applied'}
                      </span>
                    </div>
                    <div style={styles.creditCardBody}>
                      <div style={styles.creditAmount}>{formatCurrency(credit.amount)}</div>
                      <div style={styles.creditMeta}>
                        <span>{formatDate(credit.created_at)}</span>
                        {credit.status === 'applied' && credit.invoice_number && (
                          <span style={styles.creditInvoiceLink}>
                            Applied to Invoice #{credit.invoice_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>

          <div style={styles.creditsNote}>
            <p><strong>How Credits Work:</strong></p>
            <p>When a Bridge Club member redeems a free bag at your store, you earn credit for the wholesale cost. These credits are automatically applied to your next wholesale invoice.</p>
          </div>
          </>
          )}
        </div>
      )}

      {/* Payment Methods Info */}
      <div style={styles.paymentInfo}>
        <h3 style={styles.paymentInfoTitle}>Accepted Payment Methods</h3>
        <div style={styles.paymentMethodsDetailed}>
          <div style={styles.paymentMethodCard}>
            <div style={styles.paymentMethodHeader}>
              <span style={styles.paymentMethodEmoji}>&#128179;</span>
              <span style={styles.paymentMethodName}>Credit Card</span>
            </div>
            <p style={styles.paymentMethodFee}>
              A 2.9% + $0.25 processing fee will be added to your total.
            </p>
            <p style={styles.paymentMethodExample}>
              Example: $100 invoice = $103.40 charged
            </p>
          </div>
          <div style={styles.paymentMethodCard}>
            <div style={styles.paymentMethodHeader}>
              <span style={styles.paymentMethodEmoji}>&#127974;</span>
              <span style={styles.paymentMethodName}>ACH Bank Transfer</span>
              <span style={styles.recommendedBadge}>Recommended</span>
            </div>
            <p style={styles.paymentMethodFee}>
              No processing fee — ACH transfers are free!
            </p>
            <p style={styles.paymentMethodExample}>
              Example: $1,000 invoice = $1,000.00 charged
            </p>
          </div>
        </div>
        <p style={styles.paymentNote}>
          Payments are processed securely by Helcim.
        </p>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && !showPrintView && (
        <div style={styles.modalOverlay} onClick={() => { setSelectedInvoice(null); setFullInvoiceData(null); }}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Invoice {selectedInvoice.invoice_number}
                {selectedInvoice.status === 'paid' && (
                  <span style={styles.paidStamp}>PAID</span>
                )}
              </h2>
              <button onClick={() => { setSelectedInvoice(null); setFullInvoiceData(null); }} style={styles.modalClose}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.invoiceDetailGrid}>
                <div style={styles.invoiceDetailRow}>
                  <span style={styles.invoiceDetailLabel}>Order Number</span>
                  <span>#{selectedInvoice.order_number}</span>
                </div>
                <div style={styles.invoiceDetailRow}>
                  <span style={styles.invoiceDetailLabel}>Invoice Date</span>
                  <span>{formatDate(selectedInvoice.invoice_date || selectedInvoice.date)}</span>
                </div>
                <div style={styles.invoiceDetailRow}>
                  <span style={styles.invoiceDetailLabel}>Due Date</span>
                  <span style={{ color: selectedInvoice.is_overdue ? '#dc2626' : 'inherit' }}>
                    {formatDate(selectedInvoice.due_date)}
                    {selectedInvoice.is_overdue && ' (Overdue)'}
                  </span>
                </div>
                <div style={styles.invoiceDetailRow}>
                  <span style={styles.invoiceDetailLabel}>Status</span>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(selectedInvoice.status) + '20',
                    color: getStatusColor(selectedInvoice.status)
                  }}>
                    {getStatusLabel(selectedInvoice.status)}
                  </span>
                </div>
              </div>

              <div style={styles.invoiceTotals}>
                {fullInvoiceData?.totals ? (
                  <>
                    <div style={styles.invoiceTotalRow}>
                      <span>Subtotal</span>
                      <span>{formatCurrency(fullInvoiceData.totals.subtotal)}</span>
                    </div>
                    {parseFloat(fullInvoiceData.totals.promo_discount_amount || '0') > 0 && (
                      <div style={{ ...styles.invoiceTotalRow, color: '#dc2626' }}>
                        <span>Promo Discount</span>
                        <span>-{formatCurrency(fullInvoiceData.totals.promo_discount_amount)}</span>
                      </div>
                    )}
                    {parseFloat(fullInvoiceData.totals.shipping_discount || '0') > 0 && (
                      <div style={{ ...styles.invoiceTotalRow, color: '#dc2626' }}>
                        <span>Shipping Discount</span>
                        <span>-{formatCurrency(fullInvoiceData.totals.shipping_discount)}</span>
                      </div>
                    )}
                    {parseFloat(fullInvoiceData.totals.discount_amount || '0') > 0 && (
                      <div style={{ ...styles.invoiceTotalRow, color: '#dc2626' }}>
                        <span>Discount{fullInvoiceData.discount_category_name ? ` (${fullInvoiceData.discount_category_name})` : ''}</span>
                        <span>-{formatCurrency(fullInvoiceData.totals.discount_amount)}</span>
                      </div>
                    )}
                    <div style={styles.invoiceTotalRow}>
                      <span>Shipping</span>
                      <span>{formatCurrency(fullInvoiceData.totals.shipping)}</span>
                    </div>
                    <div style={styles.invoiceTotalRow}>
                      <span>Tax</span>
                      <span>{formatCurrency(fullInvoiceData.totals.tax)}</span>
                    </div>
                    <div style={{ ...styles.invoiceTotalRow, fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                      <span>Total</span>
                      <span>{formatCurrency(fullInvoiceData.totals.total)}</span>
                    </div>
                  </>
                ) : (
                  <div style={styles.invoiceTotalRow}>
                    <span>Invoice Total</span>
                    <span>{formatCurrency(selectedInvoice.total || selectedInvoice.total_amount || '0')}</span>
                  </div>
                )}
                {parseFloat(selectedInvoice.credits_applied || '0') > 0 && (
                  <div style={{ ...styles.invoiceTotalRow, color: '#7c3aed' }}>
                    <span>Credits Applied</span>
                    <span>-{formatCurrency(selectedInvoice.credits_applied)}</span>
                  </div>
                )}
                {parseFloat(selectedInvoice.paid_amount || '0') > 0 && (
                  <div style={{ ...styles.invoiceTotalRow, color: '#22c55e' }}>
                    <span>Amount Paid</span>
                    <span>-{formatCurrency(selectedInvoice.paid_amount)}</span>
                  </div>
                )}
                <div style={{
                  ...styles.invoiceTotalRow,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  borderTop: '2px solid #e5e7eb',
                  paddingTop: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  <span>Balance Due</span>
                  <span style={{ color: parseFloat(selectedInvoice.balance_due) > 0 ? '#dc2626' : '#22c55e' }}>
                    {formatCurrency(selectedInvoice.balance_due)}
                  </span>
                </div>
              </div>

              {selectedInvoice.status === 'paid' && (
                <div style={styles.paidNotice}>
                  <span style={{ fontSize: '1.5rem' }}>&#10003;</span>
                  <span>This invoice has been paid in full. Thank you!</span>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowPrintView(true)}
                disabled={fullInvoiceLoading || !fullInvoiceData}
                style={styles.viewInvoiceButton}
              >
                {fullInvoiceLoading ? 'Loading...' : 'View Full Invoice'}
              </button>
              {parseFloat(selectedInvoice.balance_due) > 0 && selectedInvoice.status !== 'draft' && (
                <button
                  onClick={() => {
                    setSelectedInvoice(null);
                    setFullInvoiceData(null);
                    handlePayNow(selectedInvoice.id);
                  }}
                  style={styles.payButtonModal}
                >
                  Pay Now
                </button>
              )}
              <button onClick={() => { setSelectedInvoice(null); setFullInvoiceData(null); }} style={styles.closeButton}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Printable Invoice Modal — uses same className as admin for @media print */}
      {selectedInvoice && showPrintView && fullInvoiceData && (
        <div className="print-modal-overlay">
          <PrintableInvoice
            data={fullInvoiceData}
            onClose={() => {
              setShowPrintView(false);
              setSelectedInvoice(null);
              setFullInvoiceData(null);
            }}
          />
        </div>
      )}

      {/* Split-payment options modal — amount + rail selection */}
      <PaymentOptionsModal
        isOpen={showPaymentModal}
        invoice={paymentModalInvoice}
        onClose={() => {
          setShowPaymentModal(false);
          setPaymentModalInvoice(null);
        }}
        onPaymentSubmit={handlePaymentSubmit}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    color: '#6b7280',
    marginTop: '0.25rem',
  },
  message: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  summaryCardWarning: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
  },
  summaryLabel: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1f2937',
  },
  filterBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  filterButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#6b7280',
  },
  filterButtonActive: {
    backgroundColor: '#2D5A27',
    border: '1px solid #2D5A27',
    color: 'white',
  },
  invoiceList: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    marginBottom: '1.5rem',
  },
  loading: {
    padding: '3rem',
    textAlign: 'center' as const,
    color: '#6b7280',
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center' as const,
    color: '#9ca3af',
  },
  emptyIcon: {
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    display: 'none',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 1rem',
    backgroundColor: '#f9fafb',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '0.9rem',
    color: '#374151',
  },
  rowOverdue: {
    backgroundColor: '#fef2f2',
  },
  overdueTag: {
    color: '#dc2626',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  payButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  mobileCards: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  mobileCard: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  mobileCardOverdue: {
    backgroundColor: '#fef2f2',
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
  },
  mobileInvoiceNum: {
    display: 'block',
    fontSize: '1rem',
    color: '#1f2937',
  },
  mobileOrder: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  mobileCardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  mobileRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.9rem',
  },
  mobileLabel: {
    color: '#6b7280',
  },
  mobileCardFooter: {
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
  },
  feeEstimates: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  feeEstimateItem: {
    display: 'inline-block',
  },
  payButtonMobile: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  paymentInfo: {
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  paymentInfoTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 1rem 0',
  },
  paymentMethods: {
    display: 'flex',
    gap: '2rem',
    flexWrap: 'wrap' as const,
    marginBottom: '1rem',
  },
  paymentMethod: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#6b7280',
  },
  paymentIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  paymentNote: {
    fontSize: '0.85rem',
    color: '#9ca3af',
    margin: 0,
  },
  paymentMethodsDetailed: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  paymentMethodCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1rem',
    border: '1px solid #e5e7eb',
  },
  paymentMethodHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  paymentMethodEmoji: {
    fontSize: '1.25rem',
  },
  paymentMethodName: {
    fontWeight: 600,
    color: '#1f2937',
    fontSize: '1rem',
  },
  recommendedBadge: {
    fontSize: '0.7rem',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontWeight: 600,
  },
  paymentMethodFee: {
    fontSize: '0.85rem',
    color: '#4b5563',
    margin: '0 0 0.25rem 0',
  },
  paymentMethodExample: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    margin: 0,
    fontStyle: 'italic' as const,
  },
  // Credits tab styles
  creditsBadge: {
    position: 'absolute' as const,
    top: '-6px',
    right: '-6px',
    backgroundColor: '#E73C3E',
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '18px',
    textAlign: 'center' as const,
  },
  creditsSection: {
    marginBottom: '1.5rem',
  },
  creditsSummaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
    marginBottom: '1rem',
  },
  creditsSummaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
  },
  creditsSummaryLabel: {
    fontSize: '0.85rem',
    color: '#6b7280',
    marginBottom: '0.25rem',
  },
  creditsSummaryValue: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1f2937',
  },
  creditsSummaryCount: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    marginTop: '0.25rem',
  },
  creditsFilterBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  creditsFilterBtn: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#6b7280',
  },
  creditsFilterBtnActive: {
    backgroundColor: '#508590',
    borderColor: '#508590',
    color: 'white',
  },
  creditsList: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  creditCard: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  creditCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem',
  },
  creditDescription: {
    display: 'block',
    fontSize: '0.95rem',
    color: '#1f2937',
  },
  creditMember: {
    display: 'block',
    fontSize: '0.85rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  },
  creditStatusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  creditCardBody: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  creditAmount: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#2D5A27',
  },
  creditMeta: {
    textAlign: 'right' as const,
    fontSize: '0.85rem',
    color: '#6b7280',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  creditInvoiceLink: {
    color: '#508590',
    fontWeight: 500,
  },
  creditsNote: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  // Invoice link and PAID badge styles
  invoiceLink: {
    background: 'none',
    border: 'none',
    color: '#508590',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 'inherit',
  },
  mobileInvoiceLink: {
    background: 'none',
    border: 'none',
    color: '#508590',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontSize: '1rem',
  },
  paidBadge: {
    display: 'inline-block',
    marginLeft: '0.5rem',
    padding: '0.125rem 0.5rem',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    fontSize: '0.7rem',
    fontWeight: 700,
    borderRadius: '4px',
    textTransform: 'uppercase' as const,
  },
  paidStamp: {
    display: 'inline-block',
    marginLeft: '1rem',
    padding: '0.25rem 0.75rem',
    backgroundColor: '#22c55e',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 700,
    borderRadius: '4px',
    textTransform: 'uppercase' as const,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  modalBody: {
    padding: '1.25rem',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.25rem',
    borderTop: '1px solid #e5e7eb',
  },
  invoiceDetailGrid: {
    display: 'grid',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  invoiceDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f3f4f6',
  },
  invoiceDetailLabel: {
    color: '#6b7280',
    fontSize: '0.9rem',
  },
  invoiceTotals: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  invoiceTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.375rem 0',
    fontSize: '0.95rem',
  },
  paidNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: '#d1fae5',
    borderRadius: '8px',
    color: '#065f46',
    fontWeight: 500,
  },
  payButtonModal: {
    padding: '0.625rem 1.25rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  closeButton: {
    padding: '0.625rem 1.25rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  viewInvoiceButton: {
    padding: '0.625rem 1.25rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  // printModalOverlay and printModalContent replaced by .print-modal-overlay CSS class
  // with @media print rules for multi-page support (matching admin invoice print)
};

// Add media query styles via CSS-in-JS workaround
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @media (min-width: 768px) {
    .billing-table { display: table !important; }
    .billing-mobile-cards { display: none !important; }
  }
  .print-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #f3f4f6;
    z-index: 1001;
    overflow: auto;
  }
  @media print {
    .print-overlay-active > *:not(.print-modal-overlay) {
      display: none !important;
    }
    .print-overlay-active .print-modal-overlay {
      position: static !important;
      background: none !important;
      overflow: visible !important;
    }
  }
`;
if (!document.querySelector('[data-billing-styles]')) {
  styleSheet.setAttribute('data-billing-styles', 'true');
  document.head.appendChild(styleSheet);
}

// Apply class names to table and mobile cards
styles.table.display = 'none';
styles.mobileCards.display = 'flex';

export default PortalBilling;
